// Photos and flyover video for one visit: a quiet grid, then a full-screen
// lightbox (swipe on phones, arrow keys on desktop). No captions on images;
// the visit date sits in the header and alt text carries the description.
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { theme, Icon, Button, STAGE, F } from './siteUi';
import { humanDate, galleryCount, stepIndex } from './siteTimelineModel';

const LB = { bg: '#0E0D0C', ink: '#F4F1EB', mute: '#8F8678', hair: 'rgba(244,241,235,0.16)' };
const lbBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, padding: 0, boxSizing: 'border-box',
  background: 'transparent', color: LB.ink, border: `1px solid ${LB.hair}`, borderRadius: 2, cursor: 'pointer', textDecoration: 'none',
};

function Lightbox({ items, index, date, onIndex, onClose }) {
  const n = items.length;
  const it = items[index];
  const closeRef = useRef(null);
  const drag = useRef(null);
  const [dx, setDx] = useState(0);
  const go = useCallback((d) => { setDx(0); onIndex(stepIndex(index, d, n)); }, [index, n, onIndex]);

  useEffect(() => {
    const prevFocus = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (closeRef.current) closeRef.current.focus();
    return () => { document.body.style.overflow = prevOverflow; if (prevFocus && prevFocus.focus) prevFocus.focus(); };
  }, []);

  useEffect(() => {
    const key = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      else if (e.key === 'ArrowRight' && n > 1) { e.preventDefault(); go(1); }
      else if (e.key === 'ArrowLeft' && n > 1) { e.preventDefault(); go(-1); }
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [go, n, onClose]);

  // Warm the neighbours so swiping feels instant.
  useEffect(() => {
    if (n < 2) return;
    [stepIndex(index, 1, n), stepIndex(index, -1, n)].forEach((j) => {
      if (items[j].type === 'image') { const im = new Image(); im.src = items[j].src; }
    });
  }, [index, items, n]);

  const down = (e) => {
    if (it.type === 'video' || e.pointerType === 'mouse' || n < 2) return;
    drag.current = { x: e.clientX, y: e.clientY, id: e.pointerId };
  };
  const move = (e) => { if (drag.current && drag.current.id === e.pointerId) setDx(e.clientX - drag.current.x); };
  const up = (e) => {
    if (!drag.current || drag.current.id !== e.pointerId) return;
    const d = e.clientX - drag.current.x;
    const dy = Math.abs(e.clientY - drag.current.y);
    drag.current = null;
    if (Math.abs(d) > 48 && Math.abs(d) > dy) go(d < 0 ? 1 : -1); else setDx(0);
  };

  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={`Photos and video, ${humanDate(date)}`} data-testid="site-lightbox" className="sg-lightbox"
      style={{ position: 'fixed', inset: 0, zIndex: 2147483000, background: LB.bg, color: LB.ink, fontFamily: F.display, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '12px max(20px, env(safe-area-inset-left)) 12px 20px', minHeight: 68, boxSizing: 'border-box' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 500 }}>{humanDate(date)}</div>
          {n > 1 ? <div data-testid="lightbox-count" style={{ fontSize: 13, color: LB.mute, fontVariantNumeric: 'tabular-nums' }}>{index + 1} of {n}</div> : null}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <a href={it.url} target="_blank" rel="noreferrer" aria-label="Download" title="Download" style={lbBtn}><Icon name="download" size={20} /></a>
          <button ref={closeRef} type="button" aria-label="Close" title="Close" onClick={onClose} style={lbBtn}><Icon name="close" size={20} /></button>
        </div>
      </div>
      <div onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
        style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', touchAction: 'pan-y', overflow: 'hidden' }}>
        {it.type === 'image' ? (
          <img key={it.id} src={it.src} alt={it.alt} draggable={false} data-testid="lightbox-image"
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', userSelect: 'none', transform: dx ? `translateX(${dx}px)` : 'none', transition: dx ? 'none' : 'transform 200ms ease' }} />
        ) : (
          <video key={it.id} src={it.src} poster={it.poster || undefined} controls playsInline preload="metadata" aria-label={it.alt} data-testid="lightbox-video"
            style={{ maxWidth: '100%', maxHeight: '100%', background: '#000' }} />
        )}
        {n > 1 ? (
          <>
            <button type="button" aria-label="Previous" onClick={() => go(-1)} className="sg-nav" style={{ ...lbBtn, position: 'absolute', left: 20, top: '50%', marginTop: -22, background: 'rgba(14,13,12,0.6)' }}><Icon name="left" size={20} /></button>
            <button type="button" aria-label="Next" onClick={() => go(1)} className="sg-nav" style={{ ...lbBtn, position: 'absolute', right: 20, top: '50%', marginTop: -22, background: 'rgba(14,13,12,0.6)' }}><Icon name="right" size={20} /></button>
          </>
        ) : null}
      </div>
      <div style={{ minHeight: 'max(20px, env(safe-area-inset-bottom))' }} />
      <style>{'.sg-lightbox[role="dialog"]{max-width:none!important;max-height:none!important;overflow:hidden!important}@media (pointer: coarse) and (max-width: 719px){.sg-nav{display:none!important}}'}</style>
    </div>,
    document.body,
  );
}

// items from galleryItems(visit); start: index to open in the lightbox right away (a video chip).
export default function SiteGallery({ items, date, tone = 'light', start = null, onClose }) {
  const t = theme(tone);
  const [open, setOpen] = useState(start);
  useEffect(() => { setOpen(start); }, [start]);
  if (!items.length) return null;
  return (
    <div data-testid="site-gallery" style={{ marginTop: 8, paddingTop: 20, borderTop: `1px solid ${t.hair}` }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 500, color: t.ink }}>Photos and video</div>
          <div style={{ fontSize: 13, color: t.mute, marginTop: 2 }}>{humanDate(date)} · {galleryCount(items)}</div>
        </div>
        <Button tone={tone} variant="secondary" compact onClick={onClose}>Close</Button>
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(46%, 220px), 1fr))', gap: 4 }}>
        {items.map((it, i) => (
          <li key={it.id} style={{ minWidth: 0 }}>
            <button type="button" onClick={() => setOpen(i)} data-testid="gallery-tile"
              aria-label={`${it.type === 'video' ? 'Play' : 'Open'} ${it.alt}`}
              style={{ position: 'relative', display: 'block', width: '100%', aspectRatio: '4 / 3', padding: 0, border: 'none', borderRadius: 2, overflow: 'hidden', cursor: 'zoom-in', background: STAGE.bg }}>
              {it.type === 'image' || it.poster ? (
                <img src={it.type === 'image' ? it.src : it.poster} alt={it.alt} loading="lazy" draggable={false}
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : null}
              {it.type === 'video' ? (
                <span aria-hidden style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: STAGE.ink }}>
                  <span style={{ display: 'inline-flex', width: 48, height: 48, alignItems: 'center', justifyContent: 'center', background: STAGE.panel, border: `1px solid ${STAGE.hair}`, borderRadius: 2 }}>
                    <Icon name="play" size={22} />
                  </span>
                </span>
              ) : null}
            </button>
          </li>
        ))}
      </ul>
      {open !== null && items[open] ? <Lightbox items={items} index={open} date={date} onIndex={setOpen} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}
